import React, { useState, useEffect, useContext } from "react";
import { getCotacoes, deleteCotacao } from "../api";
import { Trash2, Edit, Eye, CheckCircle, XCircle } from "lucide-react";
import { AuthContext } from "../AuthContext";
import { useNavigate, useSearchParams } from "react-router-dom";
import { itensPorPagina, language } from "../components/Constants";


export default function ListCotacoes() {
  const [searchParams, setSearchParams] = useSearchParams();
  const clienteParam = searchParams.get("cliente") || "";
  const statusParam = searchParams.get("status") || "";
  const buscaParam = searchParams.get("busca") || "";
  const pageParam = Number(searchParams.get("page")) || 1;
  const { user } = useContext(AuthContext);
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [msg, setMsg] = useState(null);
  const [cotacoes, setCotacoes] = useState([]);
  const [filtroStatus, setFiltroStatus] = useState(statusParam);
  const [busca, setBusca] = useState(buscaParam);
  const [paginaAtual, setPaginaAtual] = useState(pageParam);
  const [deleting, setDeleting] = useState(null);

  // Buscar detalhes da cotação
  const fetchCotacaoDetalhes = async (id) => {
    navigate(`/viewquote?cotacao=${id}`);
  };

  // Excluir cotação
  const handleDeleteCotacao = async (id) => {
    if (!window.confirm("Tem certeza que deseja excluir esta cotação?")) return;
    try {
      setDeleting(id);
      await deleteCotacao(user.token, id); // Usa função do api.js
      setCotacoes(cotacoes.filter((cotacao) => cotacao.id !== id));
      setMsg({ type: "success", text: "Cotação excluída com sucesso!" });
    } catch (error) {
      setMsg({ type: "error", text: error.message || "Erro ao excluir cotação." });
    } finally {
      setDeleting(null);
    }
  };

  useEffect(() => {
    const params = {};

    if (clienteParam) params.cliente = clienteParam;
    if (filtroStatus) params.status = filtroStatus;
    if (busca) params.busca = busca;
    if (paginaAtual > 1) params.page = paginaAtual.toString();

    setSearchParams(params);
  }, [clienteParam, filtroStatus, busca, paginaAtual, setSearchParams]);

  useEffect(() => {
    const loadData = async () => {
        // Buscar cotações
        try {
        setLoading(true);
        const data = await getCotacoes(user.token);
        // Ordenar por ID decrescente
        const sortedData = [...data].sort((a, b) => Number(b.id) - Number(a.id));
        setCotacoes(sortedData);
        //setMsg({ type: "success", text: "Cotações carregadas com sucesso!" });
        } catch (error) {
        setMsg({ type: "error", text: error.message || "Erro ao carregar cotações." });
        } finally {
        setLoading(false);
        }
    };

    loadData();
  }, [user.token]);

  const cotacoesFiltradas = cotacoes
    .filter((cotacao) =>
      clienteParam ? String(cotacao.cliente_id) === String(clienteParam) : true
    )
    .filter((cotacao) => (filtroStatus ? cotacao.status === filtroStatus : true))
    .filter((cotacao) => {
      if (!busca) return true;

      const buscaLimpa = busca.replace(/\D/g, ""); // só números

      // Se for número puro
      if (/^\d+$/.test(busca)) {
        // ID geralmente é curto
        if (busca.length <= 6) {
          return cotacao.id.toString() === busca; // EXATO (melhor que includes)
        }

        // CNPJ (11+ dígitos)
        if (busca.length >= 11) {
          const cnpjLimpo = (cotacao.cnpj || "").replace(/\D/g, "");
          return cnpjLimpo.includes(buscaLimpa);
        }
      }

      // Nome (texto)
      return (cotacao.nome_cliente || "")
        .toLowerCase()
        .includes(busca.toLowerCase());
    });

  // Paginação
  const totalPaginas = Math.ceil(cotacoesFiltradas.length / itensPorPagina());
  const cotacoesPaginadas = cotacoesFiltradas.slice(
    (paginaAtual - 1) * itensPorPagina(),
    paginaAtual * itensPorPagina()
  );

  // Formatar datas
  const formatarData = (data) => {
    if (!data) return "-";
    return new Date(data).toLocaleDateString(language());
  };

  // Cores do status
  const corStatus = (status) => {
    switch (status) {
      case "Aprovada":
        return { background: "rgba(16, 185, 129, 0.1)", color: "#10b981" };
      case "Rejeitada":
        return { background: "rgba(255, 60, 60, 0.1)", color: "#ef4444" };
      case "Enviada":
        return { background: "rgba(59, 130, 246, 0.1)", color: "#3b82f6" };
      case "Autorizada":
        return { background: "rgba(16, 185, 129, 0.1)", color: "#d114c1" };
      default:
        return { background: "rgba(234, 179, 8, 0.1)", color: "#eab308" };
    }
  };

  return (
    <div className="page-container">
      <div className="dashboard-container">
        <div className="user-card">
          <h3>Lista de Cotações</h3>

          {clienteParam && (
            <div
              style={{
                marginBottom: "1rem",
                padding: "10px 14px",
                background: "rgba(59, 130, 246, 0.1)",
                color: "#2563eb",
                borderRadius: "8px",
                fontSize: "14px",
                fontWeight: 500,
              }}
            >
              Exibindo cotações do cliente ID: {clienteParam}
              <button
                onClick={() => {
                  setSearchParams({});
                  setFiltroStatus("");
                  setBusca("");
                  setPaginaAtual(1);
                }}
                style={{
                  marginLeft: "12px",
                  background: "transparent",
                  border: "none",
                  color: "#2563eb",
                  cursor: "pointer",
                  textDecoration: "underline",
                }}
              >
                Limpar filtro
              </button>
            </div>
          )}

          {msg && (
            <div className={`error-msg ${msg.type}`}>
              {msg.text}
            </div>
          )}

          {/* Filtros e Busca */}
          <div className="form-row">
            <div className="form-group">
              <label className="labels">Buscar</label>
              <input
                type="text"
                placeholder="Número, Cliente ou CNPJ"
                value={busca}
                onChange={(e) => {
                  setBusca(e.target.value);
                  setPaginaAtual(1);
                }}
                className="form-control"
              />
            </div>
            <div className="form-group">
              <label className="labels">Status</label>
              <select
                value={filtroStatus}
                onChange={(e) => {
                  setFiltroStatus(e.target.value);
                  setPaginaAtual(1);
                }}
                className="form-control"
              >
                <option value="">Todos</option>
                <option value="Pendente">Pendente</option>
                <option value="Enviada">Enviada</option>
                <option value="Autorizada">Autorizada</option>
                <option value="Aprovada">Aprovada</option>
                <option value="Rejeitada">Rejeitada</option>
              </select>
            </div>
          </div>

          {/* INFO */}
          <div style={{ marginBottom: "10px", fontSize: "12px", color: "#666" }}>
            Total: {cotacoes.length} | Filtrados: {cotacoesFiltradas.length}
          </div>

          {loading ? (
            <div className="center-content">
              <div className="spinner"></div>
              <p>Carregando cotações...</p>
            </div>
          ) : (
            <div className="user-grid">
              {cotacoesPaginadas.length > 0 ? (
                cotacoesPaginadas.map((cotacao) => (
                  <div key={cotacao.id} className="user-card">
                    {cotacao.status === "Aprovada" ? (
                      <p><strong>Pedido n° {cotacao.id}</strong></p>
                    ) : (
                      <p><strong>Cotação n° {cotacao.id}</strong></p>
                    )}
                    <h4>{cotacao.nome_cliente || "Cliente não informado"}</h4>
                    <div className="icone-status">
                      <p style={{ 
                        color: cotacao.email_ok === 1 ? '#28a745' : '#dc3545',
                        display: 'flex',
                        alignItems: 'center',
                        gap: '0.5rem'
                      }}>
                        {cotacao.email_ok === 1 ? (
                          <>
                            <CheckCircle size={16} fill="#28a745" color="#fff" />
                            E-mail enviado
                          </>
                        ) : (
                          <>
                            <XCircle size={16} fill="#dc3545" color="#fff" />
                            E-mail não enviado
                          </>
                        )}
                      </p>
                    </div>
                    <p><strong>CNPJ:</strong> {cotacao.cnpj || "-"}</p>
                    <p><strong>Data de Criação:</strong> {formatarData(cotacao.data_criacao)}</p>
                    <p><strong>Validade:</strong> {formatarData(cotacao.validade)}</p>
                    <p>
                      <strong>Status:</strong>{" "}
                      <span
                        style={{
                          padding: "6px 12px",
                          borderRadius: "8px",
                          backgroundColor: corStatus(cotacao.status).background,
                          color: corStatus(cotacao.status).color,
                          fontSize: "0.9rem",
                          fontWeight: "500",
                        }}
                      >
                        {cotacao.status}
                      </span>
                    </p>
                    <p><strong>Total:</strong> R$ {Number(cotacao.total_valor).toLocaleString(language(), {minimumFractionDigits: 2, maximumFractionDigits: 2})}</p>
                    <p><strong>Itens:</strong> {cotacao.quantidade_itens ?? (cotacao.itens ? cotacao.itens.length : "-")}</p>
                    <p><strong>Atendente:</strong> {cotacao.nome_atendente || "-"}</p>
                    <div className="card-actions">
                      <button
                        className="btn-table btn-small"
                        onClick={() => fetchCotacaoDetalhes(cotacao.id)}
                      >
                        <Eye size={16} /> Visualizar
                      </button>
                      <button
                        className="btn-primary btn-small"
                        onClick={() => (window.location.href = `/addcotacao?cotacao=${cotacao.id}`)}
                      >
                        <Edit size={16} /> Editar
                      </button>
                      <button
                        className="btn-danger btn-small"
                        onClick={() => handleDeleteCotacao(cotacao.id)}
                        disabled={deleting === cotacao.id}
                      >
                        {deleting === cotacao.id ? (
                          <span className="spinner" style={{ width: "16px", height: "16px" }} />
                        ) : (
                          <>
                            <Trash2 size={16} /> Excluir
                          </>
                        )}
                      </button>
                    </div>
                  </div>
                ))
              ) : (
                <p style={{ textAlign: "center", color: "var(--text_color-secondary)" }}>
                  Nenhuma cotação encontrada
                </p>
              )}
            </div>
          )}

          {/* Paginação */}
          {totalPaginas > 1 && (
            <div className="pagination" style={{ display: "flex", justifyContent: "center", marginTop: "1.5rem", gap: "1rem" }}>
              <button
                onClick={() => setPaginaAtual((prev) => Math.max(prev - 1, 1))}
                disabled={paginaAtual === 1}
                className="btn-small"
                style={{ opacity: paginaAtual === 1 ? 0.6 : 1 }}
              >
                Anterior
              </button>
              <span style={{ padding: "0.5rem 1rem", color: "var(--text-color)" }}>
                Página {paginaAtual} de {totalPaginas}
              </span>
              <button
                onClick={() => setPaginaAtual((prev) => Math.min(prev + 1, totalPaginas))}
                disabled={paginaAtual === totalPaginas}
                className="btn-small"
                style={{ opacity: paginaAtual === totalPaginas ? 0.6 : 1 }}
              >
                Próxima
              </button>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}